import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  Card,
  CardContent,
  Chip,
  Stack,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider,
  Badge,
  LinearProgress,
  Fade,
  Grow
} from '@mui/material';
import {
  Undo as UndoIcon,
  Receipt as ReceiptIcon,
  LocalShipping as LocalShippingIcon,
  Home as HomeIcon,
  DirectionsCar as DirectionsCarIcon,
  Event as EventIcon,
  VpnKey as VpnKeyIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Notes as NotesIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Timeline as TimelineIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import useServices from '../services/useServices';
import moment from 'moment';
import { useTrackingStyles } from './TrackingComponent.styles';

function TrackingComponent({ steps, isLoading, invoiceId, onRevertCall, data }) {
  const classes = useTrackingStyles();
  const [notes, setNotes] = useState(data.notes || []);
  const [openModal, setOpenModal] = useState(false);
  const [openAddNoteModal, setOpenAddNoteModal] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [expanded, setExpanded] = useState({
    vehicle: false,
    pictures: false,
    notes: false
  });
  
  const isMobile = useMediaQuery('(max-width:600px)');
  const { put, post } = useServices();

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(prev => ({ ...prev, [panel]: isExpanded }));
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "--:--";
    const time = moment(timestamp, "HH:mm:ss MM/DD/YYYY");
    if (!time.isValid()) return "--:--";
    return time.format('HH:mm');
  };

  const getStepStatus = (step, index) => {
    if (step.description) return 'completed';
    if (index === 0 || (index > 0 && steps[index - 1].description)) return 'active';
    return 'pending';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon fontSize="small" />;
      case 'active': return <ScheduleIcon fontSize="small" />;
      default: return <RadioButtonUncheckedIcon fontSize="small" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'active': return 'primary';
      default: return 'default';
    }
  };

  const revertToEnroute = async () => {
    try {
      const response = await put(`ars/directCall/${invoiceId}/revertToEnroute`);
      if (response.status === 200) {
        alert(response.data);
        onRevertCall();
      } else {
        alert(response.data);
      }
    } catch (error) {
      console.log(error);
      alert(error.response.data);
    }
  };

  const handleAddNote = async () => {
    const newNoteEntry = {
      allRecords: null,
      invoiceId: invoiceId,
      notesData: newNote,
      notesDate: new Date().toISOString().split('T')[0],
      notesId: null,
      notesIndicator: 0,
      notesTime: new Date().toLocaleTimeString(),
      pageSize: 0,
      pageStartIndex: 0,
      totalInvoiceNoteCount: null,
      userId: 'user123'
    };
    
    try {
      const response = await post('ars/directCall/addNote', newNoteEntry);
      if (response.status === 200) {
        alert('Note added successfully');
        newNoteEntry['notesId'] = response.data;
        setNotes([...notes, newNoteEntry]);
        setNewNote('');
        setOpenAddNoteModal(false);
      } else {
        alert('Failed to add note');
      }
    } catch (error) {
      console.error(error);
      alert('Error adding note');
    }
  };

  const renderMobileTimeline = () => {
    const completedSteps = steps.filter(step => step.description).length;
    const progressPercentage = (completedSteps / steps.length) * 100;

    return (
      <Grow in timeout={800}>
        <Card className={classes.infoCard}>
          <CardContent className={classes.infoCardContent}>
            <Box className={classes.sectionHeader}>
              <TimelineIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 'bold', flex: 1 }}>
                Tracking Progress
              </Typography>
              <Chip 
                label={`${completedSteps}/${steps.length}`} 
                color="primary" 
                size="small"
                className={classes.badge}
              />
            </Box>
            
            {/* Progress Bar */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Overall Progress
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Math.round(progressPercentage)}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={progressPercentage}
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  backgroundColor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    background: 'linear-gradient(90deg, #4caf50 0%, #8bc34a 100%)'
                  }
                }}
              />
            </Box>

            {/* Timeline Steps */}
            <Box className={classes.statusTimeline}>
              {steps.map((step, index) => {
                const status = getStepStatus(step, index);
                const isLast = index === steps.length - 1;
                
                return (
                  <Fade in timeout={600 + (index * 200)} key={index}>
                    <Box 
                      className={`${classes.timelineStep} ${
                        status === 'completed' ? classes.timelineStepCompleted : ''
                      }`}
                    >
                      <Box 
                        className={`${classes.timelineIcon} ${
                          status === 'completed' ? classes.timelineIconCompleted :
                          status === 'active' ? classes.timelineIconActive : ''
                        }`}
                      >
                        {getStatusIcon(status)}
                      </Box>
                      
                      <Box className={classes.timelineContent}>
                        <Typography className={classes.timelineTitle}>
                          {step.label}
                        </Typography>
                        
                        <Typography className={classes.timelineTime}>
                          {step.description ? formatTime(step.description) : 'Pending'}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Chip 
                            size="small"
                            label={status.charAt(0).toUpperCase() + status.slice(1)}
                            color={getStatusColor(status)}
                            className={classes.timelineStatus}
                          />
                          
                          {step.description !== null && step.class === "arrived" && (
                            <Button
                              startIcon={<UndoIcon />}
                              size="small"
                              variant="outlined"
                              color="warning"
                              onClick={() => setOpenModal(true)}
                              className={classes.undoButton}
                            >
                              Undo
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Fade>
                );
              })}
            </Box>
          </CardContent>
        </Card>
      </Grow>
    );
  };

  const renderInvoiceCard = () => (
    <Fade in timeout={400}>
      <Card className={classes.infoCard}>
        <CardContent className={classes.infoCardContent}>
          <Box className={classes.iconText}>
            <ReceiptIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Invoice #{invoiceId}
            </Typography>
          </Box>
          
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Box className={classes.iconText}>
              <LocalShippingIcon color="secondary" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                  PICKUP LOCATION
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {data.pickup}
                </Typography>
              </Box>
            </Box>
            
            <Box className={classes.iconText}>
              <HomeIcon color="success" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                  DROP LOCATION
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {data.dropoff}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Fade>
  );

  const renderVehicleInfo = () => {
    const vehicleData = [
      { icon: <VpnKeyIcon color="primary" />, label: 'VIN', value: data.vin || 'N/A' },
      { icon: <EventIcon color="secondary" />, label: 'Year', value: data.year || 'N/A' },
      { icon: <DirectionsCarIcon color="success" />, label: 'Make', value: data.make || 'N/A' },
      { icon: <DirectionsCarIcon color="warning" />, label: 'Model', value: data.model || 'N/A' },
    ];

    if (isMobile) {
      return (
        <Fade in timeout={600}>
          <Accordion 
            expanded={expanded.vehicle} 
            onChange={handleAccordionChange('vehicle')}
            className={classes.accordion}
          >
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />}
              className={classes.accordionSummary}
            >
              <Box className={classes.sectionHeader}>
                <LocalShippingIcon sx={{ color: '#1976d2' }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Vehicle Information
                </Typography>
                <Badge 
                  badgeContent={vehicleData.filter(item => item.value !== 'N/A').length} 
                  color="primary" 
                  className={classes.badge}
                >
                  <Box />
                </Badge>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
              <Stack spacing={2}>
                {vehicleData.map((item, index) => (
                  <Box key={index} className={classes.iconText}>
                    {item.icon}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                        {item.label.toUpperCase()}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {item.value}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Fade>
      );
    }

    return (
      <Fade in timeout={600}>
        <Card className={classes.infoCard}>
          <CardContent className={classes.infoCardContent}>
            <Box className={classes.sectionHeader}>
              <LocalShippingIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Vehicle Information
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {vehicleData.map((item, index) => (
                <Box key={index} sx={{ minWidth: '45%' }} className={classes.iconText}>
                  {item.icon}
                  <Typography variant="body1">
                    <strong>{item.label}:</strong> {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Fade>
    );
  };

  const renderPicturesSection = () => {
    if (!data.pic || data.pic.length === 0) return null;

    if (isMobile) {
      return (
        <Fade in timeout={800}>
          <Accordion 
            expanded={expanded.pictures} 
            onChange={handleAccordionChange('pictures')}
            className={classes.accordion}
          >
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />}
              className={classes.accordionSummary}
            >
              <Box className={classes.sectionHeader}>
                <PhotoLibraryIcon sx={{ color: '#1976d2' }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Pictures
                </Typography>
                <Chip 
                  label={data.pic.length} 
                  color="primary" 
                  size="small"
                  className={classes.badge}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
              <Box className={classes.pictureContainer}>
                <Carousel 
                  showArrows={true} 
                  showThumbs={false} 
                  infiniteLoop={true} 
                  dynamicHeight={false}
                  showStatus={true}
                  autoPlay={false}
                  swipeable={true}
                  emulateTouch={true}
                >
                  {data.pic.map((url, index) => (
                    <div key={index}>
                      <img 
                        src={url} 
                        alt={`Vehicle Image ${index + 1}`} 
                        className={classes.picture}
                      />
                    </div>
                  ))}
                </Carousel>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Fade>
      );
    }

    return (
      <Fade in timeout={800}>
        <Card className={classes.infoCard}>
          <CardContent className={classes.infoCardContent}>
            <Box className={classes.sectionHeader}>
              <PhotoLibraryIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Pictures ({data.pic.length})
              </Typography>
            </Box>
            <Box className={classes.pictureContainer}>
              <Carousel showArrows={true} showThumbs={false} infiniteLoop={true} dynamicHeight={false}>
                {data.pic.map((url, index) => (
                  <div key={index}>
                    <img src={url} alt={`Vehicle Image ${index + 1}`} className={classes.picture} />
                  </div>
                ))}
              </Carousel>
            </Box>
          </CardContent>
        </Card>
      </Fade>
    );
  };

  const renderNotesSection = () => {
    if (isMobile) {
      return (
        <Fade in timeout={1000}>
          <Accordion 
            expanded={expanded.notes} 
            onChange={handleAccordionChange('notes')}
            className={classes.accordion}
          >
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />}
              className={classes.accordionSummary}
            >
              <Box className={classes.sectionHeader}>
                <NotesIcon sx={{ color: '#1976d2' }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Notes
                </Typography>
                <Chip 
                  label={notes.length} 
                  color="primary" 
                  size="small"
                  className={classes.badge}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
              <Stack spacing={2}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenAddNoteModal(true)}
                  fullWidth
                  className={classes.addButton}
                >
                  Add New Note
                </Button>
                
                {notes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                    No notes available
                  </Typography>
                ) : (
                  <List className={classes.notesList}>
                    {notes.map((note, index) => (
                      <React.Fragment key={note.notesId}>
                        <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                          <ListItemText
                            primary={
                              <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                                {note.notesData}
                              </Typography>
                            }
                            secondary={
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="caption" color="text.secondary">
                                  {note.notesDate} at {note.notesTime}
                                </Typography>
                                <Chip 
                                  label={`#${note.notesId}`} 
                                  size="small" 
                                  variant="outlined"
                                  sx={{ fontSize: '10px', height: '20px' }}
                                />
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < notes.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Fade>
      );
    }

    return (
      <Fade in timeout={1000}>
        <Card className={classes.infoCard}>
          <CardContent className={classes.infoCardContent}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box className={classes.sectionHeader}>
                <NotesIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Notes ({notes.length})
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setOpenAddNoteModal(true)}
                className={classes.addButton}
              >
                Add Note
              </Button>
            </Box>
            
            <TableContainer component={Paper} className={classes.notesTable}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Note ID</TableCell>
                    <TableCell>Date/Time</TableCell>
                    <TableCell>Note</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notes.map((note) => (
                    <TableRow key={note.notesId}>
                      <TableCell>#{note.notesId}</TableCell>
                      <TableCell>{note.notesDate} {note.notesTime}</TableCell>
                      <TableCell>{note.notesData}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Fade>
    );
  };

  return (
    <Box className={classes.container}>
      {/* Flatbed Request Header */}
      {data.flatbedNeeded && (
        <Fade in timeout={200}>
          <Box className={classes.flatbedHeader}>
            🚛 Flatbed Request Required
          </Box>
        </Fade>
      )}

      {/* Invoice and Location Information */}
      {renderInvoiceCard()}

      {/* Mobile Timeline or Desktop Status */}
      {isMobile ? renderMobileTimeline() : renderMobileTimeline()}

      {/* Vehicle Information */}
      {renderVehicleInfo()}

      {/* Pictures Section */}
      {renderPicturesSection()}

      {/* Notes Section */}
      {renderNotesSection()}

      {/* Modals */}
      <Dialog 
        open={openModal} 
        onClose={() => setOpenModal(false)}
        className={classes.dialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle className={classes.dialogTitle}>
          Confirm Status Revert
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to revert the delivery status back to "Enroute"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              revertToEnroute();
              setOpenModal(false);
            }}
            color="warning"
            variant="contained"
          >
            Confirm Revert
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={openAddNoteModal} 
        onClose={() => setOpenAddNoteModal(false)}
        className={classes.dialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle className={classes.dialogTitle}>
          Add New Note
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Enter your note"
            placeholder="Add any relevant information about this delivery..."
            type="text"
            fullWidth
            multiline
            rows={4}
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddNoteModal(false)} color="secondary">
            Cancel
          </Button>
          <Button 
            onClick={handleAddNote} 
            color="primary" 
            variant="contained"
            disabled={!newNote.trim()}
            className={classes.addButton}
          >
            Add Note
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TrackingComponent;